import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Table } from '@heroui/react';
import type { CatalogImportListItem } from '@/api/models';
import { countParam } from '@/pages/catalogImport/notices';
import { cn } from '@/utils/cnUtils';
import { formatInLanguage } from '@/utils/dateLocale';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    IMPORT_HISTORY_DATE_FORMAT,
    isNumericColumn,
    type ImportHistoryColumnId,
} from './importHistoryColumns';

type ImportHistoryRowProps = {
    item: CatalogImportListItem;
    columns: readonly ImportHistoryColumnId[];
};

/**
 * One import of the branch: when, which file, its rows, the products it
 * created, the units and the stock's net value it brought in, who ran it,
 * and whether it stands. An undone import stays listed, muted throughout,
 * with the time it was undone. On a phone (two columns) the date, who, the
 * figures and the undo time fold under the file.
 */
export const ImportHistoryRow: React.FC<ImportHistoryRowProps> = ({
    item,
    columns,
}) => {
    const { t, i18n } = useTranslation('pages');
    const isUndone = item.status === 'Undone';
    const tone = isUndone ? 'text-muted' : undefined;
    const formatDate = (date: string) =>
        formatInLanguage(date, IMPORT_HISTORY_DATE_FORMAT, i18n.language);
    const importedAt = formatDate(item.createdAt);
    const undoneAt = item.undoneAt ? formatDate(item.undoneAt) : null;
    const fileName =
        item.fileName ?? t('catalogImport.branchImport.history.unnamedFile');
    const userName =
        item.userName ?? t('catalogImport.branchImport.history.deletedUser');
    const isFolded = !columns.includes('date');

    const statusChip = (
        <Chip
            className="shrink-0"
            color={isUndone ? 'default' : 'success'}
            size="sm"
            variant="soft"
            {...getNodeTestIdProp(`branch-import-history-chip-${item.id}`)}
        >
            {isUndone
                ? t('catalogImport.branchImport.history.status.undone')
                : t('catalogImport.branchImport.history.status.imported')}
        </Chip>
    );

    const cellOf = (column: ImportHistoryColumnId): React.ReactNode => {
        switch (column) {
            case 'date':
                return <span className="whitespace-nowrap">{importedAt}</span>;
            case 'file':
                return (
                    <span className="flex min-w-0 flex-col gap-0.5">
                        {/* The whole name: wrapped at its hyphens and dots
                            where it can, anywhere where it must (file names
                            rarely have spaces). */}
                        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="min-w-0 wrap-anywhere">
                                {fileName}
                            </span>
                            {isFolded && isUndone ? statusChip : null}
                        </span>
                        {isFolded ? (
                            <span className="text-xs font-normal text-muted">
                                <span className="block">{`${importedAt} · ${userName}`}</span>
                                <span className="block">
                                    {t(
                                        'catalogImport.branchImport.history.figures',
                                        {
                                            rows: countParam(item.rowCount),
                                            products: countParam(
                                                item.productsCreated,
                                            ),
                                            units: countParam(item.units),
                                        },
                                    )}
                                </span>
                                {undoneAt ? (
                                    <span className="block">
                                        {t(
                                            'catalogImport.branchImport.history.undoneAt',
                                            { date: undoneAt },
                                        )}
                                    </span>
                                ) : null}
                            </span>
                        ) : null}
                    </span>
                );
            case 'rows':
                return countParam(item.rowCount);
            case 'products':
                return countParam(item.productsCreated);
            case 'units':
                return countParam(item.units);
            case 'stockValue':
                return formatGel(item.netCostTetri, { withSymbol: false });
            case 'user':
                return <span className="wrap-anywhere">{userName}</span>;
            case 'status':
                return (
                    <span className="flex flex-col items-start gap-1">
                        {statusChip}
                        {undoneAt ? (
                            <span className="whitespace-nowrap text-xs">
                                {undoneAt}
                            </span>
                        ) : null}
                    </span>
                );
        }
    };

    return (
        <Table.Row id={item.id}>
            {columns.map((column) => (
                <Table.Cell key={column}>
                    <span
                        className={cn(
                            'block tabular-nums',
                            isNumericColumn(column) &&
                                'text-end whitespace-nowrap',
                            tone,
                        )}
                        data-status={
                            column === 'file' ? item.status : undefined
                        }
                        {...getNodeTestIdProp(
                            `branch-import-history-${column}-${item.id}`,
                        )}
                    >
                        {cellOf(column)}
                    </span>
                </Table.Cell>
            ))}
        </Table.Row>
    );
};
